// RegistrationPage.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaSpinner } from "react-icons/fa";
import MD5 from "crypto-js/md5"; // Import MD5
import { API_URL } from "../components/config";
const PRIMARY = "#002966";
const ACCENT = "#ffc000";

const RegistrationPage = () => {
  const [jobTitles, setJobTitles] = useState([]);
  const [higherJobTitles, setHigherJobTitles] = useState([]);
  const [reportingUsers, setReportingUsers] = useState([]);

  const [loadingJobTitle, setLoadingJobTitle] = useState(false);
  const [loadingHigher, setLoadingHigher] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
    role: "user",
    notify: false,
    activesession: false,
    addproperty: false,
    editproperty: false,
    approverequests: false,
    delivery: false,
    status: "active",
    employee_id: "",
    company_id: "",
    branch_id: "",
    reporting_to_job_title_id: "" || "1",
    reporting_to_user_id: "" || "x",
    my_job_title_id: "",
  });

  const [error, setError] = useState("");

  useEffect(() => {
    const fetchJobTitles = async () => {
      setLoadingJobTitle(true);
      try {
        const response = await fetch(`${API_URL}/jobtitle`);
        const data = await response.json();
        setJobTitles(data);
      } catch (error) {
        setError("Failed to load job titles.");
      }
      setLoadingJobTitle(false);
    };
    fetchJobTitles();
  }, []);

  const generateUniqueId = (lastUserId) => {
    const [letters, number] = lastUserId.split("-");
    let newNumber = parseInt(number, 10) + 1;
    if (newNumber > 999999) {
      newNumber = 1;
      const newLetters = incrementLetters(letters);
      return `${newLetters}-${newNumber.toString().padStart(6, "0")}`;
    }
    return `${letters}-${newNumber.toString().padStart(6, "0")}`;
  };

  const incrementLetters = (letters) => {
    const lettersArray = letters.split("");
    let carry = true;
    for (let i = lettersArray.length - 1; i >= 0 && carry; i--) {
      if (lettersArray[i] === "Z") {
        lettersArray[i] = "A";
      } else {
        lettersArray[i] = String.fromCharCode(
          lettersArray[i].charCodeAt(0) + 1
        );
        carry = false;
      }
    }
    if (carry) {
      lettersArray.unshift("A");
    }
    return lettersArray.join("");
  };

  const handleJobTitleChange = async (e) => {
    const selectedJobTitleId = e.target.value;
    setFormData({
      ...formData,
      my_job_title_id: selectedJobTitleId,
      reporting_to_job_title_id: "",
      reporting_to_user_id: "",
    });
    setHigherJobTitles([]);
    setReportingUsers([]);
    if (!selectedJobTitleId) return;

    setLoadingHigher(true);
    try {
      const response = await fetch(
        `${API_URL}/jobtitle/higher/${selectedJobTitleId}`
      );
      const higherTitles = await response.json();
      setHigherJobTitles(higherTitles);
    } catch (error) {
      setError("Failed to fetch higher job titles.");
    }
    setLoadingHigher(false);
  };

  const handleReportingJobTitleChange = async (e) => {
    const selectedReportingJobTitleId = e.target.value;
    setFormData({
      ...formData,
      reporting_to_job_title_id: selectedReportingJobTitleId,
      reporting_to_user_id: "",
    });
    setReportingUsers([]);
    if (!selectedReportingJobTitleId) return;

    setLoadingUsers(true);
    try {
      const response = await axios.get(
        `${API_URL}/users/job_title/${selectedReportingJobTitleId}`
      );
      setReportingUsers(response.data);
    } catch (error) {
      setError("Failed to fetch reporting users.");
    }
    setLoadingUsers(false);
  };

  const handleReportingUserChange = (e) => {
    setFormData({ ...formData, reporting_to_user_id: e.target.value });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const idResponse = await fetch(`${API_URL}/users/last_user_id/`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!idResponse.ok) throw new Error("Error fetching last inserted ID");
      const idResult = await idResponse.json();
      const lastUserId = idResult[0]?.userid || "AAA-000000";
      const user_id = generateUniqueId(lastUserId);
      const newFormData = {
        ...formData,
        userId: user_id,
        password: MD5(formData.password).toString(),
      };
      const response = await axios.post(`${API_URL}/users`, newFormData);
      alert(response.data.message);
      setFormData({
        username: "",
        password: "",
        email: "",
        role: "user",
        notify: false,
        activesession: false,
        addproperty: false,
        editproperty: false,
        approverequests: false,
        delivery: false,
        status: "active",
        employee_id: "",
        company_id: "",
        branch_id: "",
        reporting_to_job_title_id: "",
        reporting_to_user_id: "",
        my_job_title_id: "",
      });
    } catch (error) {
      setError("Registration failed. Please try again.");
    }
    setSubmitting(false);
  };

  // Glass + neumorph card effect
  return (
    <div
      className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#f6f7fa] to-[#e1e5ec] relative"
      style={{
        background: "linear-gradient(120deg, #f6f7fa 0%, #e1e5ec 100%)",
      }}
    >
      {/* Accent circle decor */}
      <div
        className="absolute -top-24 -left-24 w-64 h-64 rounded-full"
        style={{ background: `${ACCENT}22`, filter: "blur(40px)", zIndex: 0 }}
      />
      <div
        className="absolute bottom-0 right-0 w-40 h-40 rounded-full"
        style={{ background: `${PRIMARY}11`, filter: "blur(32px)", zIndex: 0 }}
      />

      <form
        onSubmit={handleSubmit}
        className="relative z-10 w-full max-w-[1000px] py-10 px-7 sm:px-10
          rounded-3xl shadow-2xl
          bg-white/70 backdrop-blur-md
          border border-[#dee5f0] 
          "
        style={{
          boxShadow: "0 8px 32px 0 #00296622, 0 1.5px 5px 0 #ffc00044",
        }}
        autoComplete="off"
      >
        <h2
          className="text-3xl font-extrabold text-center mb-5"
          style={{
            color: PRIMARY,
            letterSpacing: "0.03em",
            textShadow: "0 2px 14px #00296622",
          }}
        >
          Register
        </h2>

        {/* Error */}
        {error && (
          <div className="mb-4 py-2 px-3 rounded text-sm text-[#a00] bg-[#ffc00022] border border-[#ffc00066]">
            {error}
          </div>
        )}

        {/* JOB TITLE */}
        <div className="mb-4">
          <label className="block font-medium mb-1 text-[#222]">
            Job Title
          </label>
          <div className="relative">
            <select
              name="my_job_title_id"
              value={formData.my_job_title_id}
              onChange={handleJobTitleChange}
              required
              disabled={loadingJobTitle}
              className="bg-white/80 transition-all border border-[#e0e4ef] focus:border-[#ffc000] focus:ring-2 focus:ring-[#ffc00044]
                rounded-lg px-3 py-2 pr-8 w-full outline-none shadow
                text-[#002966] font-semibold"
              style={{
                boxShadow: "0 2px 6px 0 #0029660a, 0 1.5px 2px #ffc00008",
              }}
            >
              <option value="">Select Job Title</option>
              {jobTitles.map((job) => (
                <option key={job.job_title_id} value={job.job_title_id}>
                  {job.job_title_name}
                </option>
              ))}
            </select>
            {loadingJobTitle && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#ffc000] animate-spin">
                <FaSpinner />
              </span>
            )}
          </div>
        </div>

        {/* REPORTING JOB TITLE */}
        <div className="mb-4">
          <label className="block font-medium mb-1 text-[#222]">
            Reporting To Job Title
          </label>
          <div className="relative">
            <select
              name="reporting_to_job_title_id"
              value={formData.reporting_to_job_title_id}
              onChange={handleReportingJobTitleChange}
              disabled={!formData.my_job_title_id || loadingHigher}
              className="bg-white/80 transition-all border border-[#e0e4ef] focus:border-[#ffc000] focus:ring-2 focus:ring-[#ffc00033]
                rounded-lg px-3 py-2 pr-8 w-full outline-none shadow
                text-[#002966] font-semibold"
            >
              <option value="">Select Reporting Job Title</option>
              {higherJobTitles.map((job) => (
                <option key={job.job_title_id} value={job.job_title_id}>
                  {job.job_title_name}
                </option>
              ))}
            </select>
            {loadingHigher && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#ffc000] animate-spin">
                <FaSpinner />
              </span>
            )}
          </div>
        </div>

        {/* REPORTING TO USER */}
        <div className="mb-4">
          <label className="block font-medium mb-1 text-[#222]">
            Reporting To User
          </label>
          <div className="relative">
            <select
              name="reporting_to_user_id"
              value={formData.reporting_to_user_id}
              onChange={handleReportingUserChange}
              disabled={!formData.reporting_to_job_title_id || loadingUsers}
              className="bg-white/80 transition-all border border-[#e0e4ef] focus:border-[#ffc000] focus:ring-2 focus:ring-[#ffc00033]
                rounded-lg px-3 py-2 pr-8 w-full outline-none shadow
                text-[#002966] font-semibold"
            >
              <option value="">Select User</option>
              {reportingUsers.map((user) => (
                <option key={user.userid} value={user.userid}>
                  {user.username}
                </option>
              ))}
            </select>
            {loadingUsers && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#ffc000] animate-spin">
                <FaSpinner />
              </span>
            )}
          </div>
        </div>

        {/* USERNAME */}
        <div className="mb-4">
          <label className="block font-medium mb-1 text-[#222]">Username</label>
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
            className="bg-white/80 border border-[#e0e4ef] rounded-lg px-3 py-2 w-full focus:border-[#ffc000] focus:ring-2 focus:ring-[#ffc00033] outline-none font-semibold text-[#002966] shadow"
            autoComplete="off"
          />
        </div>
        {/* PASSWORD */}
        <div className="mb-4">
          <label className="block font-medium mb-1 text-[#222]">Password</label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            className="bg-white/80 border border-[#e0e4ef] rounded-lg px-3 py-2 w-full focus:border-[#ffc000] focus:ring-2 focus:ring-[#ffc00033] outline-none font-semibold text-[#002966] shadow"
            autoComplete="new-password"
          />
        </div>
        {/* EMAIL */}
        <div className="mb-4">
          <label className="block font-medium mb-1 text-[#222]">Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="bg-white/80 border border-[#e0e4ef] rounded-lg px-3 py-2 w-full focus:border-[#ffc000] focus:ring-2 focus:ring-[#ffc00033] outline-none font-semibold text-[#002966] shadow"
            autoComplete="off"
          />
        </div>
        {/* ROLE */}
        <div className="mb-4">
          <label className="block font-medium mb-1 text-[#222]">Role</label>
          <select
            name="role"
            value={formData.role}
            onChange={handleChange}
            className="bg-white/80 border border-[#e0e4ef] rounded-lg px-3 py-2 w-full focus:border-[#ffc000] focus:ring-2 focus:ring-[#ffc00033] outline-none font-semibold text-[#002966] shadow"
          >
            <option value="admin">Admin</option>
            <option value="Human Resources">Human Resource</option>
            <option value="Support">Support</option>
            <option value="Executive">Executive</option>
          </select>
        </div>
        {/* EMPLOYEE ID */}
        <div className="mb-4">
          <label className="block font-medium mb-1 text-[#222]">
            Employee ID
          </label>
          <input
            type="text"
            name="employee_id"
            value={formData.employee_id}
            onChange={handleChange}
            className="bg-white/80 border border-[#e0e4ef] rounded-lg px-3 py-2 w-full focus:border-[#ffc000] focus:ring-2 focus:ring-[#ffc00033] outline-none font-semibold text-[#002966] shadow"
          />
        </div>
        {/* COMPANY ID */}
        <div className="mb-4">
          <label className="block font-medium mb-1 text-[#222]">
            Company ID
          </label>
          <input
            type="text"
            name="company_id"
            value={formData.company_id}
            onChange={handleChange}
            className="bg-white/80 border border-[#e0e4ef] rounded-lg px-3 py-2 w-full focus:border-[#ffc000] focus:ring-2 focus:ring-[#ffc00033] outline-none font-semibold text-[#002966] shadow"
          />
        </div>
        {/* BRANCH ID */}
        <div className="mb-6">
          <label className="block font-medium mb-1 text-[#222]">
            Branch ID
          </label>
          <input
            type="text"
            name="branch_id"
            value={formData.branch_id}
            onChange={handleChange}
            className="bg-white/80 border border-[#e0e4ef] rounded-lg px-3 py-2 w-full focus:border-[#ffc000] focus:ring-2 focus:ring-[#ffc00033] outline-none font-semibold text-[#002966] shadow"
          />
        </div>

        {/* SUBMIT */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 rounded-xl font-bold text-lg shadow
            transition-all duration-200
            bg-gradient-to-r from-[#ffc000] to-[#ffd652] text-[#002966] hover:from-[#ffd652] hover:to-[#ffc000]
            flex items-center justify-center gap-2
            active:scale-95"
          style={{
            boxShadow: "0 4px 18px #ffc00033, 0 1.5px 4px #00296622",
            letterSpacing: ".03em",
          }}
        >
          {submitting ? (
            <>
              <FaSpinner className="animate-spin" /> Registering...
            </>
          ) : (
            <>Register</>
          )}
        </button>
      </form>
    </div>
  );
};

export default RegistrationPage;
